import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchProperties } from "../lib/api";
import { PropertyCard } from "../components/PropertyCard";
import { FilterPanel } from "../components/FilterPanel";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";

const useQuery = () => new URLSearchParams(useLocation().search);

const initialFrom = (q) => ({
  transaction_type: q.get("islem") || "",
  property_type: q.get("tip") || "",
  city: q.get("city") || "",
  district: q.get("district") || "",
  min_price: q.get("min") || "",
  max_price: q.get("max") || "",
  min_area: q.get("minm") || "",
  max_area: q.get("maxm") || "",
  room_count: q.get("oda") || "",
  q: q.get("q") || "",
});

export default function Properties() {
  const q = useQuery();
  const nav = useNavigate();
  const [filters, setFilters] = useState(initialFrom(q));
  const [sort, setSort] = useState("newest");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) p[k] = v; });
    p.sort = sort;
    return p;
  }, [filters, sort]);

  useEffect(() => {
    setLoading(true);
    fetchProperties(params).then((d) => { setItems(d); setLoading(false); }).catch(() => setLoading(false));
  }, [params]);

  const clear = () => { setFilters({ transaction_type: "", property_type: "", city: "", district: "", min_price: "", max_price: "", min_area: "", max_area: "", room_count: "", q: "" }); nav("/ilanlar"); };

  return (
    <div data-testid="properties-page" className="pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <div className="label-caps mb-3">Portfolio</div>
          <h1 className="font-display text-4xl sm:text-5xl text-slate-900">Tüm İlanlar</h1>
          <p className="text-slate-600 mt-3 max-w-2xl">Satılık ve kiralık gayrimenkulleri filtreleyerek size en uygun seçeneği bulun.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            <FilterPanel filters={filters} setFilters={setFilters} onClear={clear} resultCount={items.length} />
          </div>

          <div className="lg:col-span-9">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <div className="text-sm text-slate-600"><span className="font-semibold text-slate-900">{items.length}</span> ilan gösteriliyor</div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-semibold">Sırala:</span>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger data-testid="sort-select" className="w-56 h-10 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">En Yeni İlanlar</SelectItem>
                    <SelectItem value="price_asc">Fiyat: Düşükten Yükseğe</SelectItem>
                    <SelectItem value="price_desc">Fiyat: Yüksekten Düşüğe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-[420px] rounded-2xl" />)}
              </div>
            ) : items.length === 0 ? (
              <div data-testid="empty-state" className="text-center py-24 bg-white border border-slate-200 rounded-2xl">
                <div className="font-display text-2xl text-slate-900 mb-2">Aramanıza uygun ilan bulunamadı.</div>
                <p className="text-slate-500 mb-6">Filtrelerinizi değiştirerek yeniden deneyebilirsiniz.</p>
                <button onClick={clear} data-testid="empty-clear" className="btn-accent px-6 py-3 rounded-full text-sm font-semibold">Filtreleri Temizle</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 fade-in">
                {items.map((p, i) => <PropertyCard key={p.id} p={p} idx={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
